import { useState } from "react";
import { useTranslation } from "react-i18next";
import { showNotification } from "../../../store/reducers";
import { useAppDispatch, useAppSelector } from "../../../hooks/store";
import { Modal } from "../../Modal";
import { ModalContentContainer } from "../../ModalContentContainer";
import { medicalCardTemplatesApi } from "../../../store/services/medicalCardTemplatesApi";

import {
  IMedicalCardTemplate,
  medicalCardTemplateTypeLabels,
  MedicalCardTemplateTypes,
  MedicalCardTemplateTypesEnum,
} from "../../../shared";
import { Button } from "../../Buttons";
import { ConfirmDialog } from "../ConfirmDialog";
import styles from "./medicalCardTemplatesDialod.module.scss";

interface Props {
  templateKey: MedicalCardTemplateTypes | null;
  onClose: () => void;
  onSelect: (template: {
    text: string;
    code?: string;
    id: string;
    title: string;
  }) => void;
}

export const MedicalCardTemplatesDialog = ({
  templateKey,
  onClose,
  onSelect,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(
    ({ userReducer }) => userReducer.currentUser,
  );

  const { data: templatesData, isLoading } =
    medicalCardTemplatesApi.useFetchMedicalCardTemplateListQuery(
      { type: templateKey as any },
      { skip: !templateKey },
    );

  const [createTemplate] =
    medicalCardTemplatesApi.useCreateMedicalCardTemplateMutation();
  const [updateTemplate] =
    medicalCardTemplatesApi.useUpdateMedicalCardTemplateMutation();
  const [deleteTemplate] =
    medicalCardTemplatesApi.useDeleteMedicalCardTemplateMutation();

  const templates = templatesData || [];

  const hasCodeField =
    templateKey === MedicalCardTemplateTypesEnum.ICD_10 ||
    templateKey === MedicalCardTemplateTypesEnum.ICD_9;

  const hasTextField =
    templateKey !== MedicalCardTemplateTypesEnum.TYPE_OF_CONSULTATION &&
    templateKey !== MedicalCardTemplateTypesEnum.ICD_9;

  const isTextRequired =
    !hasCodeField &&
    templateKey !== MedicalCardTemplateTypesEnum.TYPE_OF_CONSULTATION;

  const [isCreating, setIsCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    code: "",
    text: "",
    id: "",
  });

  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(
    null,
  );

  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);
  const isDeleteConfirmOpen = !!templateToDelete;

  const resetForm = () => {
    setIsCreating(false);
    setSearchQuery("");
    setEditingTemplateId(null);
    setFormData({ title: "", code: "", text: "", id: "" });
  };

  const handleUpsetTemplate = async ({
    templateId,
  }: {
    templateId?: string;
  }) => {
    try {
      const isValid = isTextRequired
        ? templateKey && formData.title && formData.text
        : templateKey && formData.title;
      if (isValid) {
        const payload = {
          type: templateKey!,
          title: formData.title,
          ...(hasCodeField ? { code: formData.code } : {}),
          ...(hasTextField && formData.text ? { text: formData.text } : {}),
        };
        if (templateId) {
          await updateTemplate({ id: templateId, params: payload }).unwrap();
        } else {
          await createTemplate(payload).unwrap();
        }
        resetForm();
      }
    } catch (error: any) {
      dispatch(
        showNotification({
          type: "error",
          message: t([
            `alert.errors.${error.data.message}`,
            `alert.somethingWentWrong`,
          ]),
        }),
      );
    }
  };

  const handleConfirmDeleteTemplate = async () => {
    if (templateToDelete) {
      try {
        await deleteTemplate(templateToDelete).unwrap();
        setTemplateToDelete(null);
      } catch (error: any) {
        dispatch(
          showNotification({
            type: "error",
            message: t([
              `alert.errors.${error.data.message}`,
              `alert.somethingWentWrong`,
            ]),
          }),
        );
        setTemplateToDelete(null);
      }
    }
  };

  const handleCancelDeleteTemplate = () => {
    setTemplateToDelete(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const filteredTemplates = templates.filter((tpl) => {
    if (!templates.length) return false;
    const query = searchQuery.toLowerCase().trim();

    const matchesTitle = tpl.title?.toLowerCase().includes(query);
    const matchesText = tpl.text?.toLowerCase().includes(query);
    const matchesCode = tpl.code
      ? tpl.code.toLowerCase().includes(query)
      : false;

    return matchesTitle || matchesText || matchesCode;
  });

  return (
    <>
      <Modal
        isOpen={!isLoading && !!templateKey}
        onClose={handleClose}
        customStyles={styles.modal}
      >
        <ModalContentContainer
          modalTitle={
            isCreating
              ? `${t("medCard.createTemplate")} · ${t(`medCard.${medicalCardTemplateTypeLabels[templateKey as MedicalCardTemplateTypes]}`)}`
              : `${t("medCard.chooseTemplate")} · ${t(`medCard.${medicalCardTemplateTypeLabels[templateKey as MedicalCardTemplateTypes]}`)}`
          }
          customStyles={styles.modal_content_container}
        >
          <ConfirmDialog
            isOpen={isDeleteConfirmOpen}
            title={t("confirmModal.confirmDeletion")}
            message={t("confirmModal.youSureDeleteTemplate")}
            onConfirm={handleConfirmDeleteTemplate}
            onCancel={handleCancelDeleteTemplate}
          />
          {!isCreating ? (
            <div className={styles.templates_list_container}>
              {templates.length > 0 && (
                <div className={styles.search_wrap}>
                  <input
                    type="text"
                    id="searchTemplates"
                    className={styles.search_input}
                    placeholder={
                      t("medCard.searchTemplatesPlaceholder") ||
                      "Search templates..."
                    }
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
                    <path d="M448 272C448 174.8 369.2 96 272 96C174.8 96 96 174.8 96 272C96 369.2 174.8 448 272 448C369.2 448 448 369.2 448 272zM407.3 430C371 461.2 323.7 480 272 480C157.1 480 64 386.9 64 272C64 157.1 157.1 64 272 64C386.9 64 480 157.1 480 272C480 323.7 461.2 371 430 407.3L571.3 548.7C577.5 554.9 577.5 565.1 571.3 571.3C565.1 577.5 554.9 577.5 548.7 571.3L407.3 430z" />
                  </svg>
                </div>
              )}

              {filteredTemplates.length > 0 ? (
                <ul className={styles.templates_list}>
                  {filteredTemplates.map((tpl: IMedicalCardTemplate) => (
                    <li
                      key={tpl._id}
                      className={styles.template_item}
                      tabIndex={0}
                      role="button"
                      onClick={() =>
                        onSelect({
                          title: tpl.title,
                          text: tpl.text || "",
                          code: tpl.code,
                          id: tpl._id,
                        })
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onSelect({
                            title: tpl.title,
                            text: tpl.text || "",
                            code: tpl.code,
                            id: tpl._id,
                          });
                        }
                      }}
                    >
                      <div className={styles.template_header}>
                        {hasCodeField && tpl.code && (
                          <span className={styles.template_code}>
                            {tpl.code}
                          </span>
                        )}
                        <span className={styles.template_title}>
                          {tpl.title}
                        </span>
                        {currentUser?._id === tpl.doctorId && (
                          <div className={styles.template_actions}>
                            <button
                              className={styles.edit_button}
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsCreating(true);
                                setEditingTemplateId(tpl._id);
                                setFormData({
                                  title: tpl.title,
                                  code: tpl.code || "",
                                  text: tpl.text || "",
                                  id: tpl._id,
                                });
                              }}
                            >
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M20.1497 7.93997L8.27971 19.81C7.21971 20.88 4.04971 21.3699 3.27971 20.6599C2.50971 19.9499 3.06969 16.78 4.12969 15.71L15.9997 3.84C16.5478 3.31801 17.2783 3.03097 18.0351 3.04019C18.7919 3.04942 19.5151 3.35418 20.0503 3.88938C20.5855 4.42457 20.8903 5.14781 20.8995 5.90463C20.9088 6.66146 20.6217 7.39189 20.0997 7.93997H20.1497Z"
                                  stroke="#000000"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M21 21H12"
                                  stroke="#000000"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                            <button
                              className={styles.delete_button}
                              onClick={(e) => {
                                e.stopPropagation();
                                setTemplateToDelete(tpl._id);
                              }}
                            >
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M4 7H20"
                                  stroke="#000000"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M6 7V18C6 19.6569 7.34315 21 9 21H15C16.6569 21 18 19.6569 18 18V7"
                                  stroke="#000000"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V7H9V5Z"
                                  stroke="#000000"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                      {tpl.text && (
                        <p className={styles.template_text}>{tpl.text}</p>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={styles.empty_text}>
                  {templates.length > 0
                    ? t("medCard.noMatchingTemplates") ||
                      "No matching templates found"
                    : t("medCard.ThereAreNoAvailableTemplates")}
                </p>
              )}
              <div className={styles.actions}>
                <Button
                  label={`+ ${t("medCard.addTemplate")}`}
                  onClick={() => setIsCreating(true)}
                  className="small_green"
                />
              </div>
            </div>
          ) : (
            <div className={styles.template_form}>
              <div className={styles.field_wrap}>
                <label>{t("medCard.templateTitle")}</label>
                <input
                  type="text"
                  value={formData.title}
                  autoFocus
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  placeholder={t("medCard.templateTitlePlaceholder")}
                />
              </div>

              {hasCodeField && (
                <div className={styles.field_wrap}>
                  <label>{t("medCard.code")}</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) =>
                      setFormData({ ...formData, code: e.target.value })
                    }
                    placeholder={t("medCard.codePlaceholder")}
                  />
                </div>
              )}

              {hasTextField && (
                <div className={styles.field_wrap}>
                  <label>{t("medCard.templateText")}</label>
                  <textarea
                    rows={4}
                    value={formData.text}
                    onChange={(e) =>
                      setFormData({ ...formData, text: e.target.value })
                    }
                    placeholder={t("medCard.templateTextPlaceholder")}
                  />
                </div>
              )}

              <div className={styles.form_actions}>
                <Button
                  label={t("buttons.cancel")}
                  onClick={resetForm}
                  className="orange"
                />

                <Button
                  label={t("buttons.save")}
                  onClick={() =>
                    handleUpsetTemplate({
                      templateId: editingTemplateId || undefined,
                    })
                  }
                  className="small_green"
                  disabled={
                    isTextRequired
                      ? !formData.title || !formData.text
                      : !formData.title
                  }
                />
              </div>
            </div>
          )}
        </ModalContentContainer>
      </Modal>
    </>
  );
};
